import { useEffect, useLayoutEffect, useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import { ChevronUp, Check, Volume2 } from 'lucide-react'
import { useVoiceStore } from '../../stores/voiceStore'

interface AudioDeviceInfo {
  deviceId: string
  label: string
}

/**
 * Master output volume slider. Sits at the top of the playback-device
 * dropdown — mirrors the "Mic input volume" slider in AudioDeviceSelector.
 *
 * Range 0–100% mapped to the existing `voiceStore.masterVolume` (0..1).
 * Drives the per-peer GainNode multiply in LiveKitBackgroundAudio — one
 * authoritative knob for "turn the whole call down a bit" without
 * touching every tile. Double-click resets to 100%.
 */
function MasterOutputVolumeSlider() {
  const masterVolume = useVoiceStore((s) => s.masterVolume)
  const setMasterVolume = useVoiceStore((s) => s.setMasterVolume)
  const pct = Math.round(masterVolume * 100)
  const label = `${pct}%`
  return (
    <div className="px-3 py-2" title="Output volume. Double-click to reset to 100%.">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px] text-muted-foreground">Output volume</span>
        <span className={`text-[11px] tabular-nums ${pct === 100 ? 'text-muted-foreground' : pct < 50 ? 'text-amber-500' : 'text-foreground'}`}>{label}</span>
      </div>
      <input
        type="range"
        min={0}
        max={1}
        step={0.01}
        value={masterVolume}
        onChange={(e) => setMasterVolume(Number.parseFloat(e.target.value))}
        onDoubleClick={() => setMasterVolume(1)}
        aria-label="Master output volume"
        className="w-full h-1 bg-muted rounded-full appearance-none cursor-pointer accent-primary"
      />
    </div>
  )
}

export default function AudioOutputDeviceSelector() {
  const { audioOutputDeviceId, setAudioOutputDevice } = useVoiceStore()
  const [devices, setDevices] = useState<AudioDeviceInfo[]>([])
  const [open, setOpen] = useState(false)
  const [supported, setSupported] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  // OSPROD-128: portal-ize the popup so it escapes the controls bar's
  // overflow-hidden ancestors and floats above the left sidebar. Same
  // pattern AudioDeviceSelector uses (OSPROD-110).
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({})
  useLayoutEffect(() => {
    if (!open) return
    const compute = () => {
      const trigger = triggerRef.current
      if (!trigger) return
      const r = trigger.getBoundingClientRect()
      const margin = 8
      const menuWidth = 240
      let right = window.innerWidth - r.right
      if (window.innerWidth - right - menuWidth < margin) {
        right = window.innerWidth - menuWidth - margin
      }
      if (right < margin) right = margin
      const bottom = window.innerHeight - r.top + margin
      setMenuStyle({ position: 'fixed', right, bottom, zIndex: 9999 })
    }
    compute()
    window.addEventListener('resize', compute)
    window.addEventListener('scroll', compute, true)
    return () => {
      window.removeEventListener('resize', compute)
      window.removeEventListener('scroll', compute, true)
    }
  }, [open])

  useEffect(() => {
    setSupported('setSinkId' in HTMLMediaElement.prototype)
  }, [])

  const loadDevices = async () => {
    try {
      const all = await navigator.mediaDevices.enumerateDevices()
      const audioOutputs = all
        .filter((d) => d.kind === 'audiooutput' && d.deviceId !== 'communications')
        .map((d) => ({ deviceId: d.deviceId, label: d.label || `Device ${d.deviceId.slice(0, 4)}` }))
      // Show a clean "Default" option instead of the raw "Default - Device Name" entry
      const filtered = audioOutputs.filter((d) => d.deviceId !== 'default')
      const devicesList = [{ deviceId: 'default', label: 'Default' }, ...filtered]
      setDevices(devicesList)

      // Auto-select default if nothing selected yet
      if (!audioOutputDeviceId) {
        const defaultDevice = audioOutputs.find((d) => d.deviceId === 'default')
        if (defaultDevice) {
          setAudioOutputDevice('default')
        } else if (audioOutputs.length > 0) {
          setAudioOutputDevice(audioOutputs[0].deviceId)
        }
      }
    } catch {
      // ignore
    }
  }

  useEffect(() => {
    if (!supported) return
    loadDevices()
    navigator.mediaDevices.addEventListener('devicechange', loadDevices)
    return () => navigator.mediaDevices.removeEventListener('devicechange', loadDevices)
  }, [supported])

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const t = e.target as Node
      // Account for the portalled menu — clicks inside it must not close.
      if (
        containerRef.current && !containerRef.current.contains(t) &&
        (!menuRef.current || !menuRef.current.contains(t))
      ) {
        setOpen(false)
      }
    }
    if (open) document.addEventListener('click', handler)
    return () => document.removeEventListener('click', handler)
  }, [open])

  const handleSelect = (deviceId: string) => {
    setAudioOutputDevice(deviceId)
    setOpen(false)
  }

  if (!supported) return null

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={triggerRef}
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v) }}
        className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-background ring-1 ring-border shadow-sm flex items-center justify-center hover:bg-accent transition z-10"
        title="Select playback device"
      >
        <ChevronUp size={12} className="text-foreground" />
      </button>

      {open && createPortal(
        <div ref={menuRef} style={menuStyle} className="bg-card rounded-lg shadow-lg ring-1 ring-border py-1 w-60">
          {/* Master output volume slider — mirrors the "Mic input volume"
              slider in AudioDeviceSelector (!455). Was a separate
              MasterVolumeControl button in the controls row before
              1.1.179A; consolidated here per user request (#6 of the
              2026-05-26 5+1-item list) so volume sits next to its device
              picker, same affordance as mic input. */}
          <MasterOutputVolumeSlider />
          <div className="px-2 py-1 text-[10px] font-semibold text-muted-foreground uppercase flex items-center gap-1 border-t border-border">
            <Volume2 size={10} />
            Playback
          </div>
          {devices.length === 0 && (
            <div className="px-3 py-2 text-xs text-muted-foreground">No devices available</div>
          )}
          {devices.map((d) => (
            <button
              key={d.deviceId}
              onClick={() => handleSelect(d.deviceId)}
              className="w-full text-left px-3 py-1.5 text-xs text-muted-foreground hover:bg-primary/90 hover:text-white transition flex items-center gap-2"
            >
              {audioOutputDeviceId === d.deviceId && <Check size={12} className="text-green-500 shrink-0" />}
              <span className="flex-1 truncate">{d.label}</span>
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  )
}
